// The phone's bottom tab bar.
//
// WHY THE CONSOLE CANNOT SIMPLY REFLOW. The desktop console is a WORKSPACE: four
// surfaces side by side — the workspace panel, the map, the conditions table and
// the forecast column — and its whole value is that you read them AGAINST each
// other. A phone cannot show four surfaces at once, so stacking them just turns
// comparison into scrolling. Reflowed honestly, the console came to 1,819px on a
// 375×812 screen: two and a bit screens, with a settings panel to scroll past
// before the map, and nothing answerable at a glance.
//
// So on a phone the four surfaces become four TABS. Only one is on screen, and
// it gets the whole viewport instead of a fifth of a long page. This is the
// shape marine and weather apps converge on, and it converges for a reason: a
// phone is held in one hand and glanced at, and a thumb reaches the bottom of
// the screen far more easily than the top.
//
// IT SHOWS AND HIDES WHAT IS ALREADY THERE. No surface is rebuilt, re-fetched or
// re-implemented for mobile — the tabs toggle which of the .appbody grid areas
// is displayed. That matters beyond effort: a phone-only copy of the conditions
// table would be a second code path that eventually disagrees with the screen it
// was copied from, which is the failure this codebase is arranged against.
//
// IN FLOW, NOT FIXED. The bar is an ordinary flex child of #app, below .appbody.
// A position: fixed bar would have to be paid for with bottom padding on every
// scrollable surface inside it, and that padding is exactly the kind of thing
// that is right on the day and wrong six months later. In flow it cannot overlap
// anything by construction; only the iPhone home-indicator inset needs saying.
//
// MapLibre needs no special handling here. main.ts already has a ResizeObserver
// on the map container, so a map revealed from display: none is resized by the
// same code that handles a dragged gutter.

import { getPref, setPref } from "./prefs.js";

/** The surfaces a phone can show, in the order they appear in the bar. */
export type MobileTab = "forecast" | "map" | "table" | "panels";

const PREF_KEY = "mobile.tab";
const TABS: readonly (readonly [MobileTab, string, string])[] = [
  // id, label, title
  ["forecast", "Forecast", "The timeline, workability and the meteograms"],
  ["map", "Map", "The workability field and the picked point"],
  ["table", "Table", "Conditions, every three hours"],
  ["panels", "Panels", "Site, SVP, fleet, tropical and the rest of the rail"],
];

/** Icons drawn rather than lettered, so the bar reads at a glance. 20×20. */
const ICONS: Record<MobileTab, string> = {
  forecast:
    '<path d="M3 15l4-5 3 3 4-6 3 4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>',
  map:
    '<path d="M3 6l4.5-2 5 2 4.5-2v12l-4.5 2-5-2L3 18z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M7.5 4v12M12.5 6v12" fill="none" stroke="currentColor" stroke-width="1.2"/>',
  table:
    '<rect x="3" y="4.5" width="14" height="11" rx="1" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M3 8.5h14M8 8.5v7M12.5 8.5v7" fill="none" stroke="currentColor" stroke-width="1.2"/>',
  panels:
    '<rect x="3" y="4.5" width="5" height="11" rx="1" fill="none" stroke="currentColor" stroke-width="1.5"/><rect x="10" y="4.5" width="7" height="11" rx="1" fill="none" stroke="currentColor" stroke-width="1.5"/>',
};

function isTab(v: unknown): v is MobileTab {
  return TABS.some(([id]) => id === v);
}

export interface MobileTabs {
  root: HTMLElement;
  /** The tab currently shown. */
  current(): MobileTab;
  /** Show a tab (also called by the rail when a workspace is opened). */
  show(tab: MobileTab): void;
}

/**
 * Build the bar and bind it to a console body.
 *
 * `body` is the .appbody element; its `data-mtab` attribute is what the
 * stylesheet keys on, so the visibility rules live in CSS with the rest of the
 * layout rather than as inline styles written from here.
 */
export function createMobileTabs(body: HTMLElement): MobileTabs {
  const root = document.createElement("nav");
  root.className = "mtabs";
  root.setAttribute("aria-label", "Console sections");

  const stored = getPref(PREF_KEY);
  // Forecast rather than Map is the default, and deliberately: the map is the
  // recognisable view but the forecast column is the one carrying the verdict,
  // and "can we work" is why somebody opens this on a phone.
  let active: MobileTab = isTab(stored) ? stored : "forecast";

  const buttons = new Map<MobileTab, HTMLButtonElement>();

  const paint = (): void => {
    body.dataset.mtab = active;
    for (const [id, b] of buttons) {
      const on = id === active;
      b.setAttribute("aria-selected", String(on));
      b.classList.toggle("on", on);
    }
  };

  const show = (tab: MobileTab): void => {
    active = tab;
    setPref(PREF_KEY, tab);
    paint();
    // A surface revealed from display: none has just been given a size for the
    // first time. Canvases and the map size themselves from their container, and
    // their observers fire on the next frame — but a chart that measured 0 while
    // hidden needs the window event some of them listen for.
    requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
  };

  for (const [id, label, title] of TABS) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "mtab";
    b.title = title;
    b.setAttribute("role", "tab");
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 20 20");
    svg.setAttribute("aria-hidden", "true");
    svg.innerHTML = ICONS[id];
    const span = document.createElement("span");
    span.textContent = label;
    b.append(svg, span);
    b.addEventListener("click", () => show(id));
    buttons.set(id, b);
    root.appendChild(b);
  }

  paint();
  return { root, current: () => active, show };
}
